import { useLoader } from '../../../hooks/useLoader';
import { useNavigate, useParams } from 'react-router-dom';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { FormProvider, Controller, FieldErrors } from 'react-hook-form';
import { FormValues, useMoshellForm } from './moshell-form';
import { useEffect, useState } from 'react';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Input, Tooltip, Button, TextArea, Select, MenuItem, Tile } from '@dsi/react-eds';
import styles from './MoshellForm.module.css';
import api from '../../../api/axiosInstance';
import { useTheme } from '../../../hooks/useTheme';
import { MoshellEndpoint, MoshellEnm } from './moshell-endpoint-interfaces';
import { AxiosError } from 'axios';
import { moshellService } from '../../../services/moshellService';
import { LOADER_DEBOUNCE_MS } from '../../../const/global-constants';

type EnvironmentCategory = 'Production' | 'Staging' | 'Development' | 'Testing';

interface Enm {
  id: string;
  name: string;
  category: string;
}

const MoshellForm = () => {
  const { theme } = useTheme();
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();
  const navigate = useNavigate();
  const [showPass, setShowPass] = useState(false);
  const [showSftpPass, setShowSftpPass] = useState(false);
  const params = useParams<{ id: string }>();
  const [formValues, setFormValues] = useState<MoshellEndpoint>();
  const [enms, setEnms] = useState<Enm[]>([]);
  const [selectedEnms, setSelectedEnms] = useState<string[]>([]);

  const categoryOptions: EnvironmentCategory[] = ['Production', 'Staging', 'Development', 'Testing'];

  const form = useMoshellForm();

  useEffect(() => {
    const fetchMoshellEndpointData = async () => {
      if (params.id) {
        showLoader();
        const response = await moshellService.getById(params.id);
        const data: MoshellEndpoint = response.data;
        setSelectedEnms(data.moshellEnms?.map((me) => me.enm_id) || []);
        const initialValues = setInitialValues(data);
        setFormValues(data);
        form.reset(initialValues);
        setTimeout(() => hideLoader(), LOADER_DEBOUNCE_MS);
      }
    };
    fetchMoshellEndpointData();
  }, [params.id]);

  useEffect(() => {
    const fetchEnms = async () => {
      try {
        showLoader();
        const response = await api.get('/api/enm');
        setEnms(response.data);
      } catch (error) {
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description:
              error && error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : 'An error occurred while fetching data.',
            timestamp: new Date(),
            color: 'red'
          }
        ]);
      } finally {
        setTimeout(() => hideLoader(), LOADER_DEBOUNCE_MS);
      }
    };
    fetchEnms();
  }, []);

  function setInitialValues(setting: MoshellEndpoint | null | undefined): FormValues | undefined {
    if (setting) {
      return {
        name: setting.name || '',
        category: setting.category || '',
        description: setting.description || '',
        endpoint: setting.endpoint || '',
        user: setting.user || '',
        password: setting.password || '',
        licensePath: setting.licensePath || '',
        sftpIp: setting.sftpIp || '',
        sftpUser: setting.sftpUser || '',
        sftpPassword: setting.sftpPassword || '',
        sftpPath: setting.sftpPath || ''
      };
    }
    return undefined;
  }

  const handleCancel = () => {
    navigate('/integrations/moshell');
  };

  const handleSave = () => {
    try {
      form.handleSubmit(
        () => {
          const dataToSend: MoshellEndpoint = setMoshellEndpoint();
          saveData(dataToSend);
        },
        (errors: FieldErrors<FormValues>) => {
          console.error('FORM ERRORS: ', errors);
        }
      )();
    } catch {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: `An unexpected error occurred while saving Moshell Endpoint.`,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  };

  const setMoshellEndpoint = (): MoshellEndpoint => {
    const values = form.getValues();
    return {
      id: formValues?.id,
      name: values.name,
      category: categoryOptions.find((c) => c === values.category) || 'Development',
      description: values.description || '',
      endpoint: values.endpoint,
      user: values.user,
      password: values.password,
      licensePath: values.licensePath,
      sftpIp: values.sftpIp,
      sftpUser: values.sftpUser,
      sftpPassword: values.sftpPassword,
      sftpPath: values.sftpPath,
      moshellEnms: selectedEnms.map((se) => {
        const obj: MoshellEnm = { moshell_id: formValues?.id || '', enm_id: se };
        return obj;
      })
    };
  };

  const saveData = async (data: MoshellEndpoint) => {
    showLoader();
    try {
      let response;
      if (data.id) {
        response = await api.put('/api/moshell-endpoint', data, { headers: { 'Content-Type': 'application/json' } });
      } else {
        response = await api.post('/api/moshell-endpoint', data, { headers: { 'Content-Type': 'application/json' } });
      }
      if (response) {
        addNotifications([
          {
            title: data.id ? `Moshell Endpoint edited successfully` : 'Moshell Endpoint Successfully Created',
            icon: 'icon-success',
            description: data.id
              ? 'Moshell Endpoint has been successfully edited.'
              : 'Moshell Endpoint has been successfully created and is ready to use.',
            timestamp: new Date(),
            color: 'green'
          }
        ]);
        navigate('/integrations/moshell');
      }
      hideLoader();
    } catch (error) {
      console.error('Error while saving Moshell Endpoint settings', error);
      hideLoader();
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: `An unexpected error occurred while saving Moshell Endpoint.`,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  };

  return (
    <>
      <ReactEDSWrapper component={Tile} id="moshell-endpoint-form" title={`${params.id ? 'Edit' : 'Create'} Moshell Endpoint`}>
        <p className={styles.subtitle}>Set up a Moshell Endpoint by defining its connection details and SFTP settings.</p>
        <form id={'moshell-endpoint-form'}>
          <FormProvider {...form}>
            <div className={`${styles.formContainer} ${styles.allWidthTextArea}`}>
              <div className="row">
                <div className={`column xl-3 lg-3 md-6 sm-12 ${styles.inputSpacing}`}>
                  <label htmlFor={'name'} className={styles.whiteText}>
                    Name <span className={styles.requiredAsterisk}> *</span>
                    <ReactEDSWrapper component={Tooltip} message="Enter a name for this Moshell endpoint." pointer>
                      <span className={styles.infoIcon}>
                        <i className="icon icon-info" />
                      </span>
                    </ReactEDSWrapper>
                  </label>
                  <Controller
                    name="name"
                    control={form.control}
                    render={({ field }) => (
                      <ReactEDSWrapper
                        component={Input}
                        id={'name'}
                        className={form.formState.errors.name ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                        type="text"
                        placeholder="Name"
                        {...field}
                      />
                    )}
                  />
                  {form.formState.errors.name && (
                    <span className={styles.error}>
                      <span className={styles.dangerIcon} aria-label="error">
                        &#9888;
                      </span>
                      {form.formState.errors.name.message as string}
                    </span>
                  )}
                </div>
                <div className={`column xl-3 lg-3 md-6 sm-12 ${styles.inputSpacing}`}>
                  <label htmlFor={'moshell-category'} className={styles.whiteText}>
                    Environment <span className={styles.requiredAsterisk}> *</span>
                    <ReactEDSWrapper component={Tooltip} message="Select the environment this endpoint belongs to." pointer>
                      <span className={styles.infoIcon}>
                        <i className="icon icon-info" />
                      </span>
                    </ReactEDSWrapper>
                  </label>
                  <Controller
                    name="category"
                    control={form.control}
                    render={({ field }) => (
                      <div>
                        <ReactEDSWrapper
                          component={Select}
                          className={`${styles.allWidthSelect}`}
                          id="moshell-category"
                          value={field.value}
                          //eslint-disable-next-line @typescript-eslint/no-explicit-any
                          onChange={(value: any) => {
                            const stringValue = Array.isArray(value) ? value[0] : value;
                            field.onChange(stringValue);
                          }}
                        >
                          {categoryOptions.map((cat) => (
                            <ReactEDSWrapper component={MenuItem} key={cat} value={cat}>
                              {cat}
                            </ReactEDSWrapper>
                          ))}
                        </ReactEDSWrapper>
                      </div>
                    )}
                  />
                  {form.formState.errors.category && (
                    <span className={styles.error}>
                      <span className={styles.dangerIcon} aria-label="error">
                        &#9888;
                      </span>
                      {form.formState.errors.category.message as string}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                  <label htmlFor={'moshell-description'} className={styles.whiteText}>
                    Description
                  </label>
                  <Controller
                    name="description"
                    control={form.control}
                    render={({ field }) => (
                      <ReactEDSWrapper
                        component={TextArea}
                        id="moshell-description"
                        rows={3}
                        placeholder="Describe the purpose of this Moshell Endpoint..."
                        {...field}
                      />
                    )}
                  />
                </div>
              </div>

              <div className={styles.methodTitle}>Connection Details</div>
              <div className="row">
                <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                  <label htmlFor={'hostname'} className={styles.whiteText}>
                    Endpoint <span className={styles.requiredAsterisk}> *</span>
                    <ReactEDSWrapper component={Tooltip} message="Enter the full URL for the Moshell connection." pointer>
                      <span className={styles.infoIcon}>
                        <i className="icon icon-info" />
                      </span>
                    </ReactEDSWrapper>
                  </label>
                  <Controller
                    name="endpoint"
                    control={form.control}
                    render={({ field }) => (
                      <ReactEDSWrapper
                        component={Input}
                        id={'hostname'}
                        className={form.formState.errors.endpoint ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                        type="text"
                        placeholder="Endpoint"
                        {...field}
                      />
                    )}
                  />
                  {form.formState.errors.endpoint && (
                    <span className={styles.error}>
                      <span className={styles.dangerIcon} aria-label="error">
                        &#9888;
                      </span>
                      {form.formState.errors.endpoint.message as string}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                  <label htmlFor={'user'} className={styles.whiteText}>
                    User Name <span className={styles.requiredAsterisk}> *</span>
                  </label>
                  <Controller
                    name="user"
                    control={form.control}
                    render={({ field }) => (
                      <ReactEDSWrapper
                        component={Input}
                        id={'user'}
                        className={form.formState.errors.user ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                        type="text"
                        placeholder="User Name"
                        {...field}
                      />
                    )}
                  />
                  {form.formState.errors.user && (
                    <span className={styles.error}>
                      <span className={styles.dangerIcon} aria-label="error">
                        &#9888;
                      </span>
                      {form.formState.errors.user.message as string}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                  <label htmlFor={'password'} className={styles.whiteText}>
                    Password <span className={styles.requiredAsterisk}> *</span>
                  </label>
                  <Controller
                    name="password"
                    control={form.control}
                    render={({ field }) => (
                      <ReactEDSWrapper
                        component={Input}
                        id={'password'}
                        className={form.formState.errors.password ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                        icon={`icon ${showPass ? 'icon-invisible' : 'icon-eye'}`}
                        onIconClick={() => setShowPass(!showPass)}
                        type={showPass ? 'text' : 'password'}
                        placeholder="Password"
                        {...field}
                      />
                    )}
                  />
                  {form.formState.errors.password && (
                    <span className={styles.error}>
                      <span className={styles.dangerIcon} aria-label="error">
                        &#9888;
                      </span>
                      {form.formState.errors.password.message as string}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                  <label htmlFor={'licensePath'} className={styles.whiteText}>
                    License Path <span className={styles.requiredAsterisk}> *</span>
                  </label>
                  <Controller
                    name="licensePath"
                    control={form.control}
                    render={({ field }) => (
                      <ReactEDSWrapper
                        component={Input}
                        id={'licensePath'}
                        className={form.formState.errors.licensePath ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                        type="text"
                        placeholder="path/to/license"
                        {...field}
                      />
                    )}
                  />
                  {form.formState.errors.licensePath && (
                    <span className={styles.error}>
                      <span className={styles.dangerIcon} aria-label="error">
                        &#9888;
                      </span>
                      {form.formState.errors.licensePath.message as string}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.methodTitle}>SFTP Configuration</div>
              <div className="row">
                <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                  <label htmlFor={'sftpIp'} className={styles.whiteText}>
                    SFTP IP <span className={styles.requiredAsterisk}> *</span>
                    <ReactEDSWrapper component={Tooltip} message="Enter a valid IPv4 address." pointer>
                      <span className={styles.infoIcon}>
                        <i className="icon icon-info" />
                      </span>
                    </ReactEDSWrapper>
                  </label>
                  <Controller
                    name="sftpIp"
                    control={form.control}
                    render={({ field }) => (
                      <ReactEDSWrapper
                        component={Input}
                        id={'sftpIp'}
                        className={form.formState.errors.sftpIp ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                        type="text"
                        placeholder="192.168.1.1"
                        {...field}
                      />
                    )}
                  />
                  {form.formState.errors.sftpIp && (
                    <span className={styles.error}>
                      <span className={styles.dangerIcon} aria-label="error">
                        &#9888;
                      </span>
                      {form.formState.errors.sftpIp.message as string}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                  <label htmlFor={'sftpUser'} className={styles.whiteText}>
                    SFTP User Name <span className={styles.requiredAsterisk}> *</span>
                  </label>
                  <Controller
                    name="sftpUser"
                    control={form.control}
                    render={({ field }) => (
                      <ReactEDSWrapper
                        component={Input}
                        id={'sftpUser'}
                        className={form.formState.errors.sftpUser ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                        type="text"
                        placeholder="SFTP User Name"
                        {...field}
                      />
                    )}
                  />
                  {form.formState.errors.sftpUser && (
                    <span className={styles.error}>
                      <span className={styles.dangerIcon} aria-label="error">
                        &#9888;
                      </span>
                      {form.formState.errors.sftpUser.message as string}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                  <label htmlFor={'sftpPassword'} className={styles.whiteText}>
                    SFTP Password <span className={styles.requiredAsterisk}> *</span>
                  </label>
                  <Controller
                    name="sftpPassword"
                    control={form.control}
                    render={({ field }) => (
                      <ReactEDSWrapper
                        component={Input}
                        id={'sftpPassword'}
                        className={form.formState.errors.sftpPassword ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                        icon={`icon ${showSftpPass ? 'icon-invisible' : 'icon-eye'}`}
                        onIconClick={() => setShowSftpPass(!showSftpPass)}
                        type={showSftpPass ? 'text' : 'password'}
                        placeholder="SFTP Password"
                        {...field}
                      />
                    )}
                  />
                  {form.formState.errors.sftpPassword && (
                    <span className={styles.error}>
                      <span className={styles.dangerIcon} aria-label="error">
                        &#9888;
                      </span>
                      {form.formState.errors.sftpPassword.message as string}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                  <label htmlFor={'sftpPath'} className={styles.whiteText}>
                    SFTP Path <span className={styles.requiredAsterisk}> *</span>
                    <ReactEDSWrapper component={Tooltip} message="Enter the file path of the SFTP server." pointer>
                      <span className={styles.infoIcon}>
                        <i className="icon icon-info" />
                      </span>
                    </ReactEDSWrapper>
                  </label>
                  <Controller
                    name="sftpPath"
                    control={form.control}
                    render={({ field }) => (
                      <ReactEDSWrapper
                        component={Input}
                        id={'sftpPath'}
                        className={form.formState.errors.sftpPath ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                        type={'text'}
                        placeholder="path/to/sftp"
                        {...field}
                      />
                    )}
                  />
                  {form.formState.errors.sftpPath && (
                    <span className={styles.error}>
                      <span className={styles.dangerIcon} aria-label="error">
                        &#9888;
                      </span>
                      {form.formState.errors.sftpPath.message as string}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </FormProvider>
        </form>
      </ReactEDSWrapper>
      <div className={`${styles.container} column sm-12`}>
        <ReactEDSWrapper component={Tile} id="linkMoshell" title={`Link this Moshell to an ENM`}>
          <p className={styles.subtitle}>You can select an existing ENM to link this ENM to it.</p>
          <div className="row">
            <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
              <ReactEDSWrapper
                component={Select}
                className={`${styles.allWidthSelect}`}
                type="input-multi"
                label="Select ENMs"
                key={`enm-select-${selectedEnms.length}`}
                value={selectedEnms}
                //eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(event: any) => {
                  const newValue = event?.target?.value ?? event;
                  console.log('Select onChange - newValue:', newValue);
                  setSelectedEnms(Array.isArray(newValue) ? newValue : []);
                }}
              >
                {enms.map((enm) => (
                  <ReactEDSWrapper key={enm.id} component={MenuItem} value={enm.id}>
                    {`${enm.name} (${enm.category})`}
                  </ReactEDSWrapper>
                ))}
              </ReactEDSWrapper>
            </div>
          </div>
        </ReactEDSWrapper>
      </div>

      <div
        className={`${styles.container} ${styles.containerPadding} ${theme ? styles.containerLight : styles.containerDark} column sm-12`}
      >
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <ReactEDSWrapper component={Button} variant="primary" className="btn secondary" type="button" onClick={handleCancel}>
            Cancel
          </ReactEDSWrapper>
          <ReactEDSWrapper component={Button} variant="primary" className="btn primary" type="button" onClick={handleSave}>
            Save
          </ReactEDSWrapper>
        </div>
      </div>
    </>
  );
};
export default MoshellForm;
